import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  Users, 
  CalendarCheck, 
  Camera,
  ListChecks,
  ArrowRight,
  Shield
} from '@phosphor-icons/react';
import Bed from '../../components/icons/RoomBedIcon';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';

export default function CoupleDashboard() {
  const { currentEvent, user, isBride, isGroom, isHost, refreshCurrentEvent } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingModule, setSavingModule] = useState('');

  const updateOptionalModule = async (moduleName, enabled) => {
    if (!currentEvent?.id || savingModule) return;
    const endpoint = moduleName === 'itinerary' ? 'itinerary-settings' : 'accommodation-settings';
    try {
      setSavingModule(moduleName);
      await api.put(`/events/${currentEvent.id}/${endpoint}`, { enabled });
      await refreshCurrentEvent();
      toast.success(`${moduleName === 'itinerary' ? 'Itinerary' : 'Accommodation'} ${enabled ? 'enabled' : 'hidden'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not update ${moduleName}`);
    } finally {
      setSavingModule('');
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentEvent?.id) return;
      try {
        const response = await api.get(`/events/${currentEvent.id}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [currentEvent?.id]);

  if (!currentEvent) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <img src="/assets/logo.png" alt="GalaGo logo" className="w-16 h-16 object-contain mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold text-wedding-charcoal mb-4">
            Welcome, {eventTypeConfig.isWedding ? (isBride ? 'Bride' : 'Groom') : eventTypeConfig.labels.hostRoleName}!
          </h1>
          <p className="text-muted-foreground">
            Your event will appear here once set up by the event manager.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const hostBasePath = isHost ? '/host' : '/couple';
  const statCards = [
    {
      title: 'Total Guests',
      value: stats?.guests?.total || 0,
      subtitle: `${stats?.guests?.confirmed || 0} confirmed`,
      icon: Users,
      color: 'bg-wedding-sage'
    },
    {
      title: 'RSVPs Pending',
      value: stats?.guests?.pending_rsvp || 0,
      subtitle: 'Awaiting response',
      icon: CalendarCheck,
      color: 'bg-wedding-gold'
    },
    {
      title: 'Rooms Assigned',
      value: stats?.rooms?.occupied || 0,
      subtitle: `of ${stats?.rooms?.total || 0} rooms`,
      icon: Bed,
      color: 'bg-wedding-rose'
    },
    {
      title: 'Tasks',
      value: stats?.tasks?.completed || 0,
      subtitle: `of ${stats?.tasks?.total || 0} completed`,
      icon: ListChecks,
      color: 'bg-wedding-charcoal'
    }
  ].filter((card) => card.title !== 'Rooms Assigned' || eventTypeConfig.features.showAccommodation);

  const eventDateRange = currentEvent.end_date
    ? `${format(new Date(currentEvent.start_date), 'MMMM d')} - ${format(new Date(currentEvent.end_date), 'MMMM d, yyyy')}`
    : format(new Date(currentEvent.start_date), 'MMMM d, yyyy');

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Hero */}
        <div 
          className="relative rounded-2xl overflow-hidden h-48 md:h-56"
          style={{ backgroundColor: currentEvent.branding?.primary_color || '#5D7061' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
          <div className="absolute inset-0 flex flex-col justify-center p-8 text-white">
            <Badge className="bg-wedding-gold text-white w-fit mb-2">
              {eventTypeConfig.isWedding ? (isBride ? 'Bride' : 'Groom') : eventTypeConfig.labels.hostRoleName}
            </Badge>
            <h1 className="font-serif text-3xl md:text-4xl font-bold" data-testid="couple-dashboard-title">
              {currentEvent.name}
            </h1>
            <p className="text-lg opacity-90 mt-1">{eventDateRange}</p>
          </div>
        </div>

        {(eventTypeConfig.features.itineraryOptional || eventTypeConfig.features.accommodationOptional) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Optional planning modules</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              {eventTypeConfig.features.itineraryOptional && (
                <div className="flex items-center justify-between gap-4 rounded-xl border p-4">
                  <div>
                    <p className="font-medium text-wedding-charcoal">Detailed itinerary</p>
                    <p className="mt-1 text-xs text-muted-foreground">Add a schedule when the celebration has multiple planned moments.</p>
                  </div>
                  <Switch
                    checked={eventTypeConfig.features.showItinerary}
                    onCheckedChange={(enabled) => updateOptionalModule('itinerary', enabled)}
                    disabled={savingModule === 'itinerary'}
                    aria-label="Enable detailed itinerary"
                  />
                </div>
              )}
              {eventTypeConfig.features.accommodationOptional && (
                <div className="flex items-center justify-between gap-4 rounded-xl border p-4">
                  <div>
                    <p className="font-medium text-wedding-charcoal">Guest accommodation</p>
                    <p className="mt-1 text-xs text-muted-foreground">Enable rooms and stay preferences only when guests need them.</p>
                  </div>
                  <Switch
                    checked={eventTypeConfig.features.showAccommodation}
                    onCheckedChange={(enabled) => updateOptionalModule('accommodation', enabled)}
                    disabled={savingModule === 'accommodation'}
                    aria-label="Enable guest accommodation"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.title} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" weight="fill" />
                </div>
                <p className="font-serif text-2xl font-bold text-wedding-charcoal">{stat.value}</p>
                <p className="text-sm font-medium text-wedding-charcoal">{stat.title}</p>
                <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-wedding-sage" />
                Guest Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to={`${hostBasePath}/guests`} className="block">
                <Button variant="outline" className="w-full justify-between">
                  View All Guests <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground">
                View guest profiles, arrival details, and RSVP status
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-wedding-gold" />
                Admin Access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to={`${hostBasePath}/guests?action=admin`} className="block">
                <Button variant="outline" className="w-full justify-between">
                  Manage Admin Roles <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground">
                Grant or revoke admin access to trusted guests
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-wedding-rose" />
                Photo Albums
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to={`${hostBasePath}/photos`} className="block">
                <Button variant="outline" className="w-full justify-between">
                  Manage Photos <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground">
                {eventTypeConfig.isWedding
                  ? 'Upload wedding photos and manage album visibility'
                  : eventTypeConfig.isBabyShower
                    ? 'Upload baby shower photos and manage album visibility'
                    : 'Upload event photos and manage album visibility'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Events */}
        {currentEvent.itinerary?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CalendarCheck className="w-5 h-5 text-wedding-sage" />
                  Upcoming Events
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  {currentEvent.itinerary.length} events
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {currentEvent.itinerary.slice(0, 4).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-wedding-sage/5">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(item.date), 'MMM d')} at {item.start_time}
                      </p>
                    </div>
                    {item.dress_code && (
                      <Badge variant="outline" className="text-wedding-gold border-wedding-gold">
                        {item.dress_code}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
